import { useState, useEffect, useCallback } from 'react';
import {
    Text,
    Flex,
    Button,
    IconButton,
    Icon,
    Box,
    Stack,
    Badge,
    FileUpload,
    Spinner,
    EmptyState,
} from '@chakra-ui/react';
import {
    DialogRoot,
    DialogContent,
    DialogHeader,
    DialogBody,
    DialogTitle,
    DialogCloseTrigger,
} from '@/components/ui/dialog';
import {
    TrashIcon,
    ArrowUpTrayIcon,
    ArrowPathIcon,
    DocumentIcon,
    ExclamationCircleIcon,
    CheckCircleIcon,
    ClockIcon,
    BookOpenIcon,
} from '@heroicons/react/24/outline';
import { KnowledgeBaseDocument, KnowledgeBaseDocumentStatus } from '@/lib/types';
import { Tooltip } from '@/components/ui/tooltip';
import { toaster } from '@/components/ui/toaster';
import DestructiveModal from '@/components/alert-dialogs/destructive-modal';
import api from '@/utils/api';

interface KnowledgeBaseModalProps {
    isOpen: boolean;
    onClose: () => void;
    workspaceId: string;
    contentTypeId: number;
    contentTypeName: string;
    canModify: boolean;
    onDocumentsChange?: () => void;
}

const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const getStatusBadge = (status: KnowledgeBaseDocumentStatus) => {
    switch (status) {
        case 'ready':
            return (
                <Badge colorPalette="green" size="sm">
                    <Icon as={CheckCircleIcon} w={3} h={3} mr={1} />
                    Ready
                </Badge>
            );
        case 'processing':
            return (
                <Badge colorPalette="blue" size="sm">
                    <Spinner size="xs" mr={1} />
                    Processing
                </Badge>
            );
        case 'pending':
            return (
                <Badge colorPalette="yellow" size="sm">
                    <Icon as={ClockIcon} w={3} h={3} mr={1} />
                    Pending
                </Badge>
            );
        case 'error':
            return (
                <Badge colorPalette="red" size="sm">
                    <Icon as={ExclamationCircleIcon} w={3} h={3} mr={1} />
                    Error
                </Badge>
            );
        default:
            return (
                <Badge colorPalette="gray" size="sm">
                    Unknown
                </Badge>
            );
    }
};

export default function KnowledgeBaseModal({
    isOpen,
    onClose,
    workspaceId,
    contentTypeId,
    contentTypeName,
    canModify,
    onDocumentsChange,
}: KnowledgeBaseModalProps) {
    const [documents, setDocuments] = useState<KnowledgeBaseDocument[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadFiles, setUploadFiles] = useState<File[]>([]);
    const [refreshingDocId, setRefreshingDocId] = useState<number | null>(null);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState<KnowledgeBaseDocument | null>(null);

    // Fetch documents when modal opens
    const fetchDocuments = useCallback(async () => {
        if (!workspaceId || !contentTypeId) return;

        setIsLoading(true);
        try {
            const url = `/knowledge-base/gemini/${workspaceId}/documents?content_type_id=${contentTypeId}`;
            const response = await api.get(url);
            const docs = response.data?.documents || [];
            // Sort by created_at descending
            const sortedDocs = [...docs].sort((a: KnowledgeBaseDocument, b: KnowledgeBaseDocument) =>
                new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            );
            setDocuments(sortedDocs);
        } catch (error: any) {
            console.error('Failed to fetch documents:', error);
            toaster.create({
                type: 'error',
                title: error.response?.data?.error || 'Failed to load documents',
            });
        } finally {
            setIsLoading(false);
        }
    }, [workspaceId, contentTypeId]);

    useEffect(() => {
        if (isOpen) {
            fetchDocuments();
        }
    }, [isOpen, fetchDocuments]);

    // Handle file upload
    const handleUploadAccept = (details: any) => {
        if (details && details.files.length > 0) {
            setUploadFiles((prev) => [...prev, ...details.files]);
        }
    };

    const handleUploadSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!workspaceId || uploadFiles.length === 0) return;

        setIsUploading(true);
        try {
            for (const file of uploadFiles) {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('content_type_id', String(contentTypeId));

                const response = await api.post(
                    `/knowledge-base/gemini/${workspaceId}/documents`,
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );

                if (response.data?.document) {
                    setDocuments(prev => [response.data.document, ...prev]);
                }
            }

            toaster.create({ title: 'Documents uploaded successfully', type: 'success' });
            setUploadFiles([]);
            onDocumentsChange?.();
        } catch (err: any) {
            toaster.create({
                type: 'error',
                title: err?.response?.data?.error || 'Failed to upload documents'
            });
        } finally {
            setIsUploading(false);
        }
    };

    // Handle document status refresh
    const handleRefreshStatus = async (doc: KnowledgeBaseDocument) => {
        setRefreshingDocId(doc.id);
        try {
            const response = await api.get(`/knowledge-base/gemini/documents/${doc.id}/status`);
            if (response.data?.document) {
                setDocuments(prev =>
                    prev.map(d => d.id === doc.id ? response.data.document : d)
                );
            }
        } catch (err: any) {
            toaster.create({
                type: 'error',
                title: err?.response?.data?.error || 'Failed to refresh status',
            });
        } finally {
            setRefreshingDocId(null);
        }
    };

    // Handle document deletion
    const handleDelete = (doc: KnowledgeBaseDocument) => {
        setDocumentToDelete(doc);
        setShowDeleteModal(true);
    };

    const onConfirmDelete = async () => {
        if (!documentToDelete) return;

        setIsDeleting(true);
        try {
            await api.delete(`/knowledge-base/gemini/documents/${documentToDelete.id}`);
            setDocuments(prev => prev.filter(d => d.id !== documentToDelete.id));
            toaster.create({ title: 'Document deleted', type: 'success' });
            onDocumentsChange?.();
        } catch (err: any) {
            toaster.create({
                type: 'error',
                title: err?.response?.data?.error || 'Failed to delete document',
            });
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
            setDocumentToDelete(null);
        }
    };

    return (
        <>
            <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && onClose()} size="xl">
                <DialogContent maxW="800px" maxH="85vh" overflow="hidden">
                    <DialogHeader>
                        <DialogTitle>
                            <Flex align="center" gap={2}>
                                <Icon as={BookOpenIcon} w={5} h={5} />
                                {contentTypeName} - Knowledge Base
                            </Flex>
                        </DialogTitle>
                        <DialogCloseTrigger />
                    </DialogHeader>
                    <DialogBody pb={6} overflow="auto">
                        {/* Upload Section */}
                        {canModify && (
                            <Box mb={6} p={4} bg="gray.50" borderRadius="md">
                                <form onSubmit={handleUploadSubmit}>
                                    <Stack gap={3}>
                                        <Text fontSize="sm" fontWeight="medium" color="gray.700">
                                            Upload documents
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                            PDF, Word, Excel, PowerPoint, text, CSV, Markdown, JSON (max 50MB)
                                        </Text>
                                        <FileUpload.Root
                                            maxW="full"
                                            alignItems="stretch"
                                            maxFileSize={50 * 1024 * 1024}
                                            onFileAccept={handleUploadAccept}
                                            accept={{
                                                'application/pdf': ['.pdf'],
                                                'application/msword': ['.doc'],
                                                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                                                'application/vnd.ms-excel': ['.xls'],
                                                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                                                'application/vnd.ms-powerpoint': ['.ppt'],
                                                'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
                                                'text/plain': ['.txt'],
                                                'text/csv': ['.csv'],
                                                'text/markdown': ['.md'],
                                                'application/json': ['.json'],
                                            }}
                                        >
                                            <FileUpload.HiddenInput />
                                            <FileUpload.Dropzone minHeight="80px">
                                                <Icon as={ArrowUpTrayIcon} w={4} h={4} />
                                                <FileUpload.DropzoneContent>
                                                    <Box fontSize="sm">Drag and drop or click to upload</Box>
                                                </FileUpload.DropzoneContent>
                                            </FileUpload.Dropzone>
                                            <FileUpload.List />
                                        </FileUpload.Root>
                                        <Flex gap={2}>
                                            <Button
                                                type="submit"
                                                colorPalette="brand"
                                                size="sm"
                                                loading={isUploading}
                                                disabled={uploadFiles.length === 0}
                                            >
                                                Upload
                                            </Button>
                                            {uploadFiles.length > 0 && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setUploadFiles([])}
                                                >
                                                    Clear
                                                </Button>
                                            )}
                                        </Flex>
                                    </Stack>
                                </form>
                            </Box>
                        )}

                        {/* Documents List */}
                        <Box>
                            <Flex justify="space-between" align="center" mb={3}>
                                <Text fontSize="sm" fontWeight="medium" color="gray.700">
                                    Documents ({documents.length})
                                </Text>
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    onClick={fetchDocuments}
                                    loading={isLoading}
                                >
                                    <Icon as={ArrowPathIcon} w={4} h={4} />
                                    Refresh
                                </Button>
                            </Flex>

                            {isLoading ? (
                                <Flex justify="center" py={8}>
                                    <Spinner size="lg" />
                                </Flex>
                            ) : documents.length === 0 ? (
                                <EmptyState.Root>
                                    <EmptyState.Content>
                                        <EmptyState.Indicator>
                                            <Icon as={DocumentIcon} w={8} h={8} />
                                        </EmptyState.Indicator>
                                        <EmptyState.Title>No documents</EmptyState.Title>
                                        <EmptyState.Description>
                                            Upload documents to your knowledge base to enhance AI content generation.
                                        </EmptyState.Description>
                                    </EmptyState.Content>
                                </EmptyState.Root>
                            ) : (
                                <Stack gap={2}>
                                    {documents.map((doc) => (
                                        <Box
                                            key={doc.id}
                                            p={3}
                                            bg="white"
                                            border="1px solid"
                                            borderColor="gray.200"
                                            borderRadius="md"
                                        >
                                            <Flex justify="space-between" align="flex-start" gap={3}>
                                                <Flex direction="column" gap={1} flex={1} minW={0}>
                                                    <Text
                                                        fontSize="sm"
                                                        fontWeight="medium"
                                                        color="gray.700"
                                                        overflow="hidden"
                                                        textOverflow="ellipsis"
                                                        whiteSpace="nowrap"
                                                        title={doc.original_filename}
                                                    >
                                                        {doc.original_filename}
                                                    </Text>
                                                    <Flex gap={3} align="center" flexWrap="wrap">
                                                        <Text fontSize="xs" color="gray.500">
                                                            {formatFileSize(doc.file_size_bytes)}
                                                        </Text>
                                                        <Text fontSize="xs" color="gray.400">
                                                            {formatDate(doc.created_at)}
                                                        </Text>
                                                    </Flex>
                                                    {doc.error_message && (
                                                        <Text fontSize="xs" color="red.500">
                                                            Error: {doc.error_message}
                                                        </Text>
                                                    )}
                                                </Flex>
                                                <Flex align="center" gap={2}>
                                                    {getStatusBadge(doc.status)}
                                                    {(doc.status === 'pending' || doc.status === 'processing') && (
                                                        <Tooltip content="Refresh Status">
                                                            <IconButton
                                                                aria-label="Refresh Status"
                                                                variant="ghost"
                                                                size="xs"
                                                                onClick={() => handleRefreshStatus(doc)}
                                                                loading={refreshingDocId === doc.id}
                                                            >
                                                                <Icon as={ArrowPathIcon} w={4} h={4} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {canModify && (
                                                        <Tooltip content="Delete">
                                                            <IconButton
                                                                aria-label="Delete"
                                                                variant="ghost"
                                                                size="xs"
                                                                colorPalette="red"
                                                                onClick={() => handleDelete(doc)}
                                                            >
                                                                <Icon as={TrashIcon} w={4} h={4} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Flex>
                                            </Flex>
                                        </Box>
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    </DialogBody>
                </DialogContent>
            </DialogRoot>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && documentToDelete && (
                <DestructiveModal
                    isOpen={showDeleteModal}
                    onCancel={() => setShowDeleteModal(false)}
                    onConfirm={onConfirmDelete}
                    isLoading={isDeleting}
                    title="Delete Document"
                    confirmText="Delete"
                >
                    {`You are about to delete "${documentToDelete.original_filename}". This will also remove it from Gemini. Press 'Delete' to continue.`}
                </DestructiveModal>
            )}
        </>
    );
}
